import * as THREE from 'three';
import type { SketchArcObject } from '../../../mesh/sketch/SketchArcObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenArcRelationRuntimeRecord,
	ArcReferenceProjectionState,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import type { ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenArcOnCurveConstraint } from './constraints/ReferenceDrivenArcOnCurveConstraint';
import type {
	ReferenceDrivenArcRelationBinding,
	ReferenceDrivenArcRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenArcRelationService';

export type {
	ReferenceDrivenArcRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenArcRelationService';

/**
 * 生成圆弧引用的投影状态。
 *
 * @param targetArc 目标圆弧。
 * @returns 圆弧投影状态。
 */
function createArcProjectionState(targetArc: SketchArcObject): ArcReferenceProjectionState {
	const definition = targetArc.getArcDefinition();

	return {
		projectionResultKind: 'arc',
		startAngle: definition.startAngle,
		endAngle: definition.endAngle,
		clockwise: definition.clockwise,
	};
}

/**
 * 参考驱动圆弧关系服务。
 */
export class ReferenceDrivenArcRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenArcRelationRuntimeRecord,
		ReferenceDrivenArcRelationBinding
	> {
	/**
	 * 挂载圆弧 on-curve 约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'arc' }>;
		targetArc: SketchArcObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenArcRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetArc: input.targetArc,
		});

		const record: ReferenceDrivenArcRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'on-curve',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetArc.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenArcOnCurveConstraint({
			referenceId: input.reference.referenceId,
			projectedCenterWorld: new THREE.Vector3(...input.projectionResult.geometryPayload.centerWorld),
			projectedRadius: input.projectionResult.geometryPayload.radius,
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetArc.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetArc: input.targetArc,
		});

		/**
		 * 起止角与走向是多解分支提示，必须在统一求解前从当前候选几何冻结；
		 * 挂载阶段不再通过对象 trigger 改写端点。
		 */
		record.projectionState = createArcProjectionState(input.targetArc);
		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除圆弧关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetArc?: SketchArcObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetArc.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetArc) {
			const constraint = input.targetArc.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenArcOnCurveConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetArc.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
